import { useCallback, useState } from "react";
import { apiClient } from "@/services/api/axiosClient";
import { API_ENDPOINTS } from "@/services/api/endpoints";
import { toastService } from "@/shared/toast";

type ApiResponseLike<T> = {
  status?: "success" | "error";
  success?: boolean;
  message?: string;
  error?: string;
  data?: T;
};

export type Subscription = {
  subscription_id?: string;
  propertyId?: string;
  propertyAddress?: string;
  status?: string;
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: string;
  annualFee?: number | string;
  [key: string]: unknown;
};

type SubscriptionsListResponse = {
  subscriptions: Subscription[];
};

const isSuccess = (payload: ApiResponseLike<unknown>): boolean => {
  if (payload.status) {
    return payload.status === "success";
  }
  return !!payload.success;
};

export const useSubscriptions = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [cancelingId, setCancelingId] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<ApiResponseLike<SubscriptionsListResponse>>(
        API_ENDPOINTS.PROPERTY.SUBSCRIPTIONS_LIST
      );

      if (isSuccess(data)) {
        setSubscriptions(data.data?.subscriptions || []);
      } else {
        setSubscriptions([]);
      }
    } catch (err) {
      console.error("Error fetching subscriptions:", err);
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const cancelSubscription = useCallback(
    async (subscriptionId: string): Promise<{ success: boolean; message?: string }> => {
      setCancelingId(subscriptionId);
      try {
        const { data } = await apiClient.post<ApiResponseLike<{ success?: boolean }>>(
          API_ENDPOINTS.PROPERTY.SUBSCRIPTIONS_CANCEL,
          { subscription_id: subscriptionId }
        );

        if (isSuccess(data)) {
          await refresh();
          const message = data.message || "Subscription cancelled successfully";
          toastService.show(message, "success");
          return { success: true, message };
        }

        const message = data?.message || data?.error || "Failed to cancel subscription";
        toastService.show(message, "error");
        return { success: false, message };
      } catch (err) {
        console.error("Cancel subscription error:", err);
        const message = err instanceof Error ? err.message : "An unexpected error occurred.";
        toastService.show(message, "error");
        return { success: false, message };
      } finally {
        setCancelingId(null);
      }
    },
    [refresh]
  );

  return {
    subscriptions,
    loading,
    cancelingId,
    refresh,
    cancelSubscription,
  } as const;
};
